'use client'

import { useState, useMemo, useRef } from 'react'
import { format, differenceInDays } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@paitox/ui/lib/utils'
import {
  Search, Plus, ChevronRight, Phone, Mail, Star,
  CalendarDays, DollarSign, TrendingUp,
  Clock, CheckCircle2, XCircle, AlertCircle,
  ArrowLeft, Edit, Trash2, ShieldCheck, Heart, User, ClipboardList, PenTool, Image, Eye, MessageSquare, Send, Sparkles, Check
} from 'lucide-react'
import { db, Client as DbClient, Appointment as DbAppointment, ClientSessionZone as DbSessionZone, ClientFichaMedica as DbFicha, ClientPhotoProgress as DbPhotoProgress, ClientDocument as DbDocument } from '../lib/db'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TAG_COLORS: Record<string, string> = {
  VIP:       'bg-amber-50 text-amber-700 border border-amber-200',
  Frecuente: 'bg-blue-50 text-blue-700 border border-blue-200',
  Nueva:     'bg-emerald-50 text-emerald-700 border border-emerald-200',
  Facial:    'bg-cyan-50 text-cyan-700 border border-cyan-200',
  Masajes:   'bg-violet-50 text-violet-700 border border-violet-200',
  Uñas:      'bg-rose-50 text-rose-700 border border-rose-200',
  Inactivo:  'bg-slate-100 text-slate-600 border border-slate-300'
}

const STATUS_STYLE = {
  confirmed:   { icon: CheckCircle2, color: 'text-blue-600',    bg: 'bg-blue-50',    label: 'Confirmada' },
  pending:     { icon: AlertCircle,  color: 'text-amber-500',   bg: 'bg-amber-50',   label: 'Por Confirmar' },
  in_progress: { icon: Clock,        color: 'text-cyan-600',    bg: 'bg-cyan-50',    label: 'En Sala' },
  completed:   { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Completada' },
  cancelled:   { icon: XCircle,      color: 'text-rose-500',    bg: 'bg-rose-50',    label: 'Cancelada' },
}

const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`
const fmtDate = (d: Date) => format(d, "d 'de' MMM yyyy", { locale: es })
const avatarColors = ['bg-cyan-500','bg-violet-500','bg-rose-500','bg-blue-500','bg-amber-500','bg-emerald-500','bg-indigo-500']
const getAvatarColor = (id: string) => {
  const numeric = parseInt(id.replace(/\D/g, '')) || 0
  return avatarColors[numeric % avatarColors.length]
}

// ─── Client Detail Panel ──────────────────────────────────────────────────────

function ClientDetail({ client, history, onBack, onSaveClient }: { client: DbClient; history: DbAppointment[]; onBack: () => void; onSaveClient: (updated: DbClient) => void }) {
  const clientHistory = history.filter(h => h.clientId === client.id).sort((a,b) => new Date(b.start).getTime() - new Date(a.start).getTime())
  const completedVisits = clientHistory.filter(h => h.status === 'completed')
  const avgSpend = completedVisits.length ? Math.round(completedVisits.reduce((s,h) => s + h.price, 0) / completedVisits.length) : 0

  const [activeTab, setActiveTab] = useState<'info' | 'ficha' | 'sessions' | 'photos' | 'docs'>('info')

  // Ficha Form State
  const initialFicha: DbFicha = client.ficha || {
    rut: '', birthDate: '', gender: 'Femenino', address: '', comuna: '', cellphone: client.phone || '', email: client.email || '',
    pendingBalance: 0, hta: 'No', dbt: 'No', neuropathic: 'No', medications: '', allergies: '', neoplasiaHistory: 'No',
    sunReaction: 'Tipo III', varices: 'No', otherComments: '', freeTrial: 'No'
  }
  const [ficha, setFicha] = useState<DbFicha>(initialFicha)
  const [isFichaSaved, setIsFichaSaved] = useState(false)

  // Sessions/Zones Form State
  const [sessionZones, setSessionZones] = useState<DbSessionZone[]>(client.sessionZones || [])
  const [newZoneName, setNewZoneName] = useState('')

  // Interactive Signature State (#7)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [documents, setDocuments] = useState<DbDocument[]>(client.documents || [])

  // Photos Progression State (#4)
  const [photosProgress, setPhotosProgress] = useState<DbPhotoProgress[]>(client.photosProgress || [])
  const [photoBefore, setPhotoBefore] = useState('')
  const [photoAfter, setPhotoAfter] = useState('')
  const [photoNotes, setPhotoNotes] = useState('')
  const [photoAptId, setPhotoAptId] = useState('')

  // Satisfaction Survey state (#5)
  const [surveyRating, setSurveyRating] = useState<number | null>(null)
  const [surveyLink, setSurveyLink] = useState('')
  const [npsAlert, setNpsRedirect] = useState(false)

  const handleSaveFicha = () => {
    const updatedClient = { ...client, ficha }
    onSaveClient(updatedClient)
    setIsFichaSaved(true)
    setTimeout(() => setIsFichaSaved(false), 3000)
  }

  // Session zones helpers (#2, #5)
  const handleAddZone = () => {
    if (!newZoneName) return
    const updatedZones = [...sessionZones, { zoneName: newZoneName, rows: [] }]
    setSessionZones(updatedZones)
    onSaveClient({ ...client, sessionZones: updatedZones })
    setNewZoneName('')
  }

  const handleAddSessionRow = (zoneIndex: number) => {
    const zonesCopy = [...sessionZones]
    const currentRows = zonesCopy[zoneIndex].rows
    const nextSessionNum = currentRows.length + 1
    const newRow = {
      date: format(new Date(), 'yyyy-MM-dd'),
      sessionNum: nextSessionNum,
      skinType: 'III',
      hairType: 'Medio',
      fluency: 10,
      energy: 8,
      operator: 'Ana María',
      comments: ''
    }
    zonesCopy[zoneIndex].rows = [...currentRows, newRow]
    setSessionZones(zonesCopy)
    onSaveClient({ ...client, sessionZones: zonesCopy })
  }

  const handleUpdateSessionRow = (zoneIndex: number, rowIndex: number, key: string, val: any) => {
    const zonesCopy = [...sessionZones]
    zonesCopy[zoneIndex].rows[rowIndex] = {
      ...zonesCopy[zoneIndex].rows[rowIndex],
      [key]: val
    }
    setSessionZones(zonesCopy)
    onSaveClient({ ...client, sessionZones: zonesCopy })
  }

  // Signature canvas handlers (#7)
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.beginPath()
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
    setIsDrawing(true)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    ctx.stroke()
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  const saveSignatureAndDoc = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dataUrl = canvas.toDataURL('image/png')

    const newDoc: DbDocument = {
      id: `doc-${Date.now()}`,
      title: `Consentimiento y Firma Médica (${format(new Date(), 'dd/MM/yyyy')})`,
      createdAt: new Date().toISOString(),
      signatureDataUrl: dataUrl
    }

    const updatedDocs = [newDoc, ...documents]
    setDocuments(updatedDocs)
    onSaveClient({ ...client, documents: updatedDocs })
    clearCanvas()
  }

  // Satisfaction Survey generation (#5)
  const generateSurvey = () => {
    const generated = `http://paitox.cl/survey/c-${client.id}/srv-1`
    setSurveyLink(generated)
  }

  const submitSurvey = (rating: number) => {
    setSurveyRating(rating)
    if (rating >= 9) {
      setNpsRedirect(true)
    }
  }

  // Photos progression handlers (#4)
  const handleAddPhotoProgress = () => {
    if (!photoBefore) return
    const matchedApt = clientHistory.find(h => h.id === photoAptId) || clientHistory[0]

    const newPhoto: DbPhotoProgress = {
      id: `ph-${Date.now()}`,
      appointmentId: photoAptId || matchedApt?.id || 'manual',
      appointmentDate: matchedApt ? format(new Date(matchedApt.start), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'),
      serviceName: matchedApt ? matchedApt.service : 'Tratamiento Estético',
      photoBeforeUrl: photoBefore,
      photoAfterUrl: photoAfter || 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=150',
      notes: photoNotes
    }

    const updatedPhotos = [newPhoto, ...photosProgress]
    setPhotosProgress(updatedPhotos)
    onSaveClient({ ...client, photosProgress: updatedPhotos })

    // Reset fields
    setPhotoBefore('')
    setPhotoAfter('')
    setPhotoNotes('')
  }

  const inp = 'w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-400/40'

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-white shrink-0">
        <button onClick={onBack} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className={cn('h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 shadow', getAvatarColor(client.id))}>
          {client.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-slate-800">{client.firstName} {client.lastName}</h2>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {client.tags.map(t => (
              <span key={t} className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full', TAG_COLORS[t] || 'bg-slate-100 text-slate-600')}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-slate-200 px-5 shrink-0">
        {[
          { id: 'info', label: 'Historial', icon: Clock },
          { id: 'ficha', label: 'Ficha Clínica', icon: ClipboardList },
          { id: 'sessions', label: 'Zonas Láser', icon: Heart },
          { id: 'photos', label: 'Antes & Después', icon: Image },
          { id: 'docs', label: 'Firmas/Consentimientos', icon: PenTool }
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={cn("flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all mr-2",
              activeTab === t.id ? "border-cyan-600 text-cyan-600 font-bold" : "border-transparent text-slate-500 hover:text-slate-700"
            )}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      <div className="flex-1 overflow-auto p-5">
        {activeTab === 'info' && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'Visitas totales', value: client.totalVisits, icon: CalendarDays, color: 'text-blue-600 bg-blue-50' },
                { label:'Total gastado',   value: fmtCLP(client.totalSpent), icon: DollarSign,  color: 'text-emerald-600 bg-emerald-50' },
                { label:'Promedio/visita', value: fmtCLP(avgSpend),  icon: TrendingUp,  color: 'text-cyan-600 bg-cyan-50' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-3.5 flex items-center gap-2.5">
                  <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center shrink-0 shadow-inner', s.color)}>
                    <s.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 font-semibold leading-none">{s.label}</p>
                    <p className="text-sm font-bold text-slate-800 mt-0.5">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Satisfaction rating & unique link generator (#5) */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Star className="h-4 w-4 text-cyan-500" />
                    Encuestas de Satisfacción Automatizadas (NPS)
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Genera enlaces de satisfacción únicos para recolectar opiniones. Si el cliente otorga una nota alta, el sistema le invitará a dejar una reseña en Google.
                  </p>
                </div>
                <button onClick={generateSurvey} className="px-3 h-8 text-[11px] font-bold rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white shrink-0">
                  Generar Enlace Único
                </button>
              </div>

              {surveyLink && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded-lg space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-600">Link de uso único:</span>
                    <span className="font-mono text-cyan-600 font-bold select-all">{surveyLink}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Simular Calificación de Cliente:</p>
                    <div className="flex gap-1.5">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n} onClick={() => submitSurvey(n)}
                          className={cn("h-7 w-7 rounded-lg border text-xs font-bold transition", surveyRating === n ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 hover:bg-slate-50")}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  {npsAlert && (
                    <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 p-3 rounded-lg text-xs space-y-1">
                      <span className="font-bold block flex items-center gap-1"><Sparkles className="h-4 w-4 text-emerald-500" /> ¡Calificación Excelente Redirigida!</span>
                      <p>
                        El cliente otorgó una nota de **{surveyRating}/10**. Redirigiendo automáticamente a **https://g.page/r/your-google-profile-slug/review** para captar reseña en Google Maps.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Visit Log list */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Historial de visitas ({clientHistory.length})</h3>
              {clientHistory.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-xl">Sin visitas registradas aún</div>
              ) : (
                <div className="space-y-2">
                  {clientHistory.map(h => {
                    const st = STATUS_STYLE[h.status] || STATUS_STYLE.pending
                    return (
                      <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-3 shadow-sm">
                        <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center shrink-0', st.bg)}>
                          <st.icon className={cn('h-4 w-4', st.color)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <p className="font-semibold text-sm text-slate-800">{h.service}</p>
                              <p className="text-xs text-slate-500">{h.staff} · {h.room}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-bold text-sm text-slate-800">{fmtCLP(h.price)}</p>
                              <p className="text-[10px] text-slate-400">{fmtDate(new Date(h.start))}</p>
                            </div>
                          </div>
                          {h.notes && <p className="text-xs text-slate-500 mt-1.5 italic">{h.notes}</p>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ficha Clínica (#2, #5) */}
        {activeTab === 'ficha' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <ClipboardList className="h-4 w-4 text-cyan-600" />
                Anamnésis y Ficha de Tratamiento
              </h3>
              <button onClick={handleSaveFicha} className="px-3 h-8 text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Guardar Ficha
              </button>
            </div>

            {isFichaSaved && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-xs font-semibold">
                Ficha clínica guardada exitosamente en la memoria del sistema.
              </div>
            )}

            {/* Form layout matching the image fields exactly */}
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-xl space-y-3">
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">Ficha Personal</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">RUT</label>
                    <input type="text" className={inp} value={ficha.rut} onChange={e => setFicha({...ficha, rut: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Fecha Nacimiento</label>
                    <input type="date" className={inp} value={ficha.birthDate} onChange={e => setFicha({...ficha, birthDate: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Sexo</label>
                    <select className={inp} value={ficha.gender} onChange={e => setFicha({...ficha, gender: e.target.value})}>
                      <option>Femenino</option>
                      <option>Masculino</option>
                      <option>Otro</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Dirección</label>
                    <input type="text" className={inp} value={ficha.address} onChange={e => setFicha({...ficha, address: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Comuna</label>
                    <input type="text" className={inp} value={ficha.comuna} onChange={e => setFicha({...ficha, comuna: e.target.value})} />
                  </div>
                </div>
              </div>

              <div className="bg-amber-50/50 p-4 rounded-xl space-y-3 border border-amber-100">
                <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider">Anamnésis</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Hipertensión Arterial (HTA)</label>
                    <select className={inp} value={ficha.hta} onChange={e => setFicha({...ficha, hta: e.target.value})}>
                      <option>No</option>
                      <option>Sí, controlada</option>
                      <option>Sí, no controlada</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Diabetes (DBT)</label>
                    <select className={inp} value={ficha.dbt} onChange={e => setFicha({...ficha, dbt: e.target.value})}>
                      <option>No</option>
                      <option>Sí, Tipo I</option>
                      <option>Sí, Tipo II</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Neuropatía</label>
                    <select className={inp} value={ficha.neuropathic} onChange={e => setFicha({...ficha, neuropathic: e.target.value})}>
                      <option>No</option>
                      <option>Sí</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Medicamentos habituales</label>
                    <input type="text" className={inp} value={ficha.medications} onChange={e => setFicha({...ficha, medications: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Alergias conocidas</label>
                    <input type="text" className={inp} value={ficha.allergies} onChange={e => setFicha({...ficha, allergies: e.target.value})} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Antecedentes de Neoplasia</label>
                    <input type="text" className={inp} value={ficha.neoplasiaHistory} onChange={e => setFicha({...ficha, neoplasiaHistory: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Reacción de piel al sol</label>
                    <select className={inp} value={ficha.sunReaction} onChange={e => setFicha({...ficha, sunReaction: e.target.value})}>
                      <option>Tipo I - Siempre se quema, nunca se broncea</option>
                      <option>Tipo II - Se quema fácilmente, bronceado mínimo</option>
                      <option>Tipo III - Piel sensible, bronceado gradual</option>
                      <option>Tipo IV - Se quema mínimamente, bronceado rápido</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-500 mb-1 block">Várices</label>
                    <select className={inp} value={ficha.varices} onChange={e => setFicha({...ficha, varices: e.target.value})}>
                      <option>No</option>
                      <option>Sí, grado leve</option>
                      <option>Sí, grado severo</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Prueba Gratis</label>
                  <input type="text" className={inp} value={ficha.freeTrial} onChange={e => setFicha({...ficha, freeTrial: e.target.value})} />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Otros comentarios médicos relevantes</label>
                  <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 focus:outline-none" rows={3} value={ficha.otherComments} onChange={e => setFicha({...ficha, otherComments: e.target.value})} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Laser Session Zone Tracking (#2, #5) */}
        {activeTab === 'sessions' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 flex-wrap gap-2">
              <div>
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Heart className="h-4 w-4 text-rose-500 animate-pulse" />
                  Control de Zonas de Sesión de Depilación Láser
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Frecuencia, fluencia y energía aplicada por sesión</p>
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder="Nueva zona (Ej: Axilas)" className="h-8 border border-slate-200 rounded-lg px-2 text-xs" value={newZoneName} onChange={e => setNewZoneName(e.target.value)} />
                <button onClick={handleAddZone} className="px-2.5 h-8 text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-1 shrink-0">
                  <Plus className="h-3.5 w-3.5" /> Agregar Zona
                </button>
              </div>
            </div>

            {sessionZones.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs">Ninguna zona registrada de momento. Agrega una arriba.</div>
            ) : (
              <div className="space-y-6">
                {sessionZones.map((zone, zIndex) => (
                  <div key={zone.zoneName} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex justify-between items-center">
                      <span className="font-extrabold text-xs text-slate-700 uppercase tracking-widest">Zona: {zone.zoneName}</span>
                      <button onClick={() => handleAddSessionRow(zIndex)}
                        className="h-7 px-2.5 bg-white border border-slate-200 text-[10px] font-bold text-cyan-600 hover:bg-slate-50 rounded-lg flex items-center gap-1 shadow-sm">
                        <Plus className="h-3 w-3" /> Registrar Sesión {zone.rows.length + 1}
                      </button>
                    </div>

                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 text-slate-500 font-bold border-b border-slate-100">
                          <th className="p-2.5">Fecha</th>
                          <th className="p-2.5">N° Sesión</th>
                          <th className="p-2.5">Piel</th>
                          <th className="p-2.5">Pelo</th>
                          <th className="p-2.5">Fluencia</th>
                          <th className="p-2.5">Energía</th>
                          <th className="p-2.5">Operador</th>
                          <th className="p-2.5">Comentarios de Cabina</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {zone.rows.map((row, rIndex) => (
                          <tr key={rIndex} className="hover:bg-slate-50/40">
                            <td className="p-2.5">
                              <input type="date" className="h-7 border-slate-200 rounded text-[11px] bg-slate-50 p-1 w-24" value={row.date} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'date', e.target.value)} />
                            </td>
                            <td className="p-2.5 font-bold text-slate-700">{row.sessionNum}</td>
                            <td className="p-2.5">
                              <select className="h-7 border-slate-200 rounded text-[11px] bg-slate-50" value={row.skinType} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'skinType', e.target.value)}>
                                <option>I</option><option>II</option><option>III</option><option>IV</option>
                              </select>
                            </td>
                            <td className="p-2.5">
                              <select className="h-7 border-slate-200 rounded text-[11px] bg-slate-50" value={row.hairType} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'hairType', e.target.value)}>
                                <option>Grueso</option><option>Medio</option><option>Fino</option>
                              </select>
                            </td>
                            <td className="p-2.5">
                              <input type="number" className="h-7 border-slate-200 rounded text-[11px] bg-slate-50 p-1 w-12" value={row.fluency} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'fluency', Number(e.target.value))} />
                            </td>
                            <td className="p-2.5">
                              <input type="number" className="h-7 border-slate-200 rounded text-[11px] bg-slate-50 p-1 w-12" value={row.energy} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'energy', Number(e.target.value))} />
                            </td>
                            <td className="p-2.5">
                              <input type="text" className="h-7 border-slate-200 rounded text-[11px] bg-slate-50 p-1 w-20" value={row.operator} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'operator', e.target.value)} />
                            </td>
                            <td className="p-2.5">
                              <input type="text" placeholder="Detalles de cabina..." className="h-7 border-slate-200 rounded text-[11px] bg-slate-50 p-1 w-full" value={row.comments || ''} onChange={e => handleUpdateSessionRow(zIndex, rIndex, 'comments', e.target.value)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Antes y Después Photos Tracking (#4) */}
        {activeTab === 'photos' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="pb-3 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Image className="h-4 w-4 text-cyan-600" />
                  Galería de Evolución (Antes & Después)
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Visualización y control de cambios visuales por cita</p>
              </div>
            </div>

            {/* Creation block */}
            <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-3">
              <span className="text-xs font-bold text-slate-700 block">Agregar Nuevas Capturas</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Referencia a Cita</label>
                  <select className={inp} value={photoAptId} onChange={e => setPhotoAptId(e.target.value)}>
                    <option value="">Seleccione cita asociada...</option>
                    {clientHistory.map(h => (
                      <option key={h.id} value={h.id}>{h.service} ({format(new Date(h.start), 'dd/MM/yyyy')})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Notas de la evolución</label>
                  <input type="text" className={inp} placeholder="Ej: Disminución de hiperpigmentación" value={photoNotes} onChange={e => setPhotoNotes(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Foto Antes (URL simulada)</label>
                  <input type="text" className={inp} placeholder="https://images.unsplash.com/..." value={photoBefore} onChange={e => setPhotoBefore(e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Foto Después (URL simulada)</label>
                  <input type="text" className={inp} placeholder="https://images.unsplash.com/..." value={photoAfter} onChange={e => setPhotoAfter(e.target.value)} />
                </div>
              </div>
              <button onClick={handleAddPhotoProgress} disabled={!photoBefore} className="px-3.5 h-8 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-bold transition disabled:opacity-40">
                Guardar Comparación
              </button>
            </div>

            {/* Photos display */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {photosProgress.map(ph => (
                <div key={ph.id} className="border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm bg-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-xs font-bold text-slate-800 block">{ph.serviceName}</span>
                      <span className="text-[10px] text-slate-400">Fecha: {ph.appointmentDate}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative rounded-lg overflow-hidden border border-slate-200">
                      <img src={ph.photoBeforeUrl} alt="Antes" className="h-32 w-full object-cover" />
                      <span className="absolute bottom-1 left-1 bg-black/70 text-[9px] text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Antes</span>
                    </div>
                    <div className="relative rounded-lg overflow-hidden border border-slate-200">
                      <img src={ph.photoAfterUrl} alt="Después" className="h-32 w-full object-cover" />
                      <span className="absolute bottom-1 left-1 bg-cyan-600 text-[9px] text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Después</span>
                    </div>
                  </div>
                  {ph.notes && (
                    <p className="text-xs text-slate-600 italic bg-slate-50 p-2 rounded-lg border border-slate-100">{ph.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Digital Signature & Documents Generation (#7) */}
        {activeTab === 'docs' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="pb-3 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <PenTool className="h-4 w-4 text-cyan-600" />
                  Consentimientos Firmados Digitalmente
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Captura de firmas en tablet y generación de actas</p>
              </div>
            </div>

            {/* Signature Draw Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                <span className="text-xs font-bold text-slate-700 block">Firma en Tablet / Local</span>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Por favor, solicite al cliente firmar con el dedo o lápiz dentro del recuadro para validar el consentimiento médico.
                </p>
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <canvas ref={canvasRef} width={320} height={140} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={() => setIsDrawing(false)}
                    className="w-full cursor-crosshair h-[140px]" />
                </div>
                <div className="flex gap-2">
                  <button onClick={clearCanvas} className="px-3 h-8 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-100 transition text-slate-600">
                    Limpiar
                  </button>
                  <button onClick={saveSignatureAndDoc} className="px-3 h-8 bg-cyan-600 text-white rounded-lg text-xs font-bold hover:bg-cyan-700 transition flex items-center gap-1.5">
                    <PenTool className="h-3.5 w-3.5" /> Generar Documento PDF
                  </button>
                </div>
              </div>

              {/* Documents Log */}
              <div className="space-y-3">
                <span className="text-xs font-bold text-slate-700 block">Fichas y Documentos Emitidos</span>
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="border border-slate-100 rounded-lg p-3 bg-white flex items-center justify-between shadow-sm">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">{doc.title}</span>
                        <span className="text-[10px] text-slate-400 block">Fecha: {new Date(doc.createdAt).toLocaleDateString('es-CL')}</span>
                        {doc.signatureDataUrl && (
                          <div className="mt-2 border border-slate-200 rounded bg-slate-50 p-1 inline-block">
                            <span className="text-[8px] font-bold text-slate-400 block uppercase mb-1">Firma del cliente:</span>
                            <img src={doc.signatureDataUrl} alt="Firma" className="h-10 object-contain max-w-[120px]" />
                          </div>
                        )}
                      </div>
                      <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 px-2 py-1 rounded border border-emerald-100 uppercase flex items-center gap-1">
                        <Check className="h-3 w-3" /> Firmado
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ClientsPage() {
  const [clients, setClients] = useState<DbClient[]>(() => db.getClients())
  const [search, setSearch] = useState('')
  const [filterTag, setFilterTag] = useState<string | null>(null)
  const [selectedClient, setSelectedClient] = useState<DbClient | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [sortBy, setSortBy] = useState<'name' | 'visits' | 'spent'>('visits')

  // Inactivity threshold (#10)
  const [inactiveDaysConfig] = useState(() => db.getSettings().inactiveThresholdDays || 45)

  // Message drawer state (#1, #8, #10)
  const [whatsappClient, setWhatsappClient] = useState<DbClient | null>(null)
  const templates = useMemo(() => db.getCampaignTemplates(), [])
  const [selectedTemplateId, setSelectedTemplateId] = useState(templates[0]?.id || '')

  // Form Fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [tagInput, setTagInput] = useState('')

  const allTags = useMemo(() => {
    const list = Array.from(new Set(clients.flatMap(c => c.tags)))
    if (!list.includes('Inactivo')) list.push('Inactivo')
    return list
  }, [clients])

  const history = useMemo(() => db.getAppointments(), [])

  // Dynamic tags classification (#2, #10)
  const processedClients = useMemo(() => {
    return clients.map(c => {
      const copyTags = [...c.tags]
      if (c.lastVisit) {
        const daysIn = differenceInDays(new Date(), new Date(c.lastVisit))
        if (daysIn > inactiveDaysConfig) {
          if (!copyTags.includes('Inactivo')) copyTags.push('Inactivo')
        } else {
          const idx = copyTags.indexOf('Inactivo')
          if (idx !== -1) copyTags.splice(idx, 1)
        }
      }
      return { ...c, tags: copyTags }
    })
  }, [clients, inactiveDaysConfig])

  const filtered = useMemo(() => {
    let list = processedClients
    if (search) list = list.filter(c => `${c.firstName} ${c.lastName} ${c.phone} ${c.email}`.toLowerCase().includes(search.toLowerCase()))
    if (filterTag) list = list.filter(c => c.tags.includes(filterTag))
    list = [...list].sort((a,b) =>
      sortBy === 'name'   ? a.firstName.localeCompare(b.firstName) :
      sortBy === 'visits' ? b.totalVisits - a.totalVisits :
      b.totalSpent - a.totalSpent
    )
    return list
  }, [processedClients, search, filterTag, sortBy])

  const handleCreate = () => {
    if (!firstName || !lastName) return

    const newClient: DbClient = {
      id: `c-${Date.now()}`,
      firstName,
      lastName,
      phone: phone || '+56900000000',
      email: email || `${firstName.toLowerCase()}@paitox-guest.cl`,
      tags: tagInput ? tagInput.split(',').map(t => t.trim()) : [],
      avatar: (firstName[0] + lastName[0]).toUpperCase(),
      joinedAt: new Date().toISOString(),
      notes,
      totalVisits: 0,
      totalSpent: 0,
      lastVisit: null
    }

    const updated = [newClient, ...clients]
    setClients(updated)
    db.saveClients(updated)
    setShowNew(false)

    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setNotes('')
    setTagInput('')
  }

  const handleSaveSingleClient = (updated: DbClient) => {
    const updatedList = clients.map(c => c.id === updated.id ? updated : c)
    setClients(updatedList)
    db.saveClients(updatedList)
    setSelectedClient(updated)
  }

  const simulateSendMessage = () => {
    const tmpl = templates.find(t => t.id === selectedTemplateId)
    if (!tmpl || !whatsappClient) return

    const personalized = tmpl.content.replace('{{name}}', whatsappClient.firstName)

    // Trigger simulated notification bell (#6)
    const currentNotifs = db.getNotifications()
    const newNotif = {
      id: `not-${Date.now()}`,
      text: `Mensaje WhatsApp enviado con éxito a ${whatsappClient.firstName} ${whatsappClient.lastName}`,
      time: 'Hace un momento',
      unread: true
    }
    db.saveNotifications([newNotif, ...currentNotifs])

    setWhatsappClient(null)
  }

  if (selectedClient) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4">
        <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <ClientDetail client={selectedClient} history={history} onBack={() => setSelectedClient(null)} onSaveClient={handleSaveSingleClient} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Clientes</h1>
          <p className="text-xs text-slate-500">{clients.length} clientes registrados</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          <Plus className="h-4 w-4" /> Nuevo cliente
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar clientes..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {allTags.map(tag => (
            <button key={tag} onClick={() => setFilterTag(t => t === tag ? null : tag)}
              className={cn('px-2.5 h-8 rounded-lg text-xs font-semibold border transition-all',
                filterTag === tag ? TAG_COLORS[tag] || 'bg-slate-800 text-white border-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300')}>
              {tag}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
          className="h-9 px-3 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 bg-white focus:outline-none">
          <option value="visits">Más visitas</option>
          <option value="spent">Más gastado</option>
          <option value="name">Nombre A-Z</option>
        </select>
      </div>

      {/* Client list */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(client => (
            <div key={client.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md hover:border-cyan-200 transition-all group flex flex-col justify-between">
              <div className="flex items-start gap-3 cursor-pointer" onClick={() => setSelectedClient(client)}>
                <div className={cn('h-11 w-11 rounded-full flex items-center justify-center text-white font-bold shrink-0 shadow', getAvatarColor(client.id))}>
                  {client.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-slate-800 truncate">{client.firstName} {client.lastName}</h3>
                    {client.tags.includes('VIP') && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 shrink-0" />}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {client.tags.map(t => (
                      <span key={t} className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full', TAG_COLORS[t] || 'bg-slate-100 text-slate-600')}>{t}</span>
                    ))}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-cyan-500 transition-colors shrink-0 mt-1" />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2">
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 font-medium">Visitas</p>
                  <p className="text-sm font-bold text-slate-700">{client.totalVisits}</p>
                </div>
                <div className="text-center border-x border-slate-100">
                  <p className="text-[10px] text-slate-400 font-medium">Gastado</p>
                  <p className="text-sm font-bold text-slate-700">{fmtCLP(client.totalSpent)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 font-medium">Última visita</p>
                  <p className="text-[11px] font-semibold text-slate-600">
                    {client.lastVisit ? format(new Date(client.lastVisit), 'd MMM', { locale: es }) : '—'}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>
                <button onClick={() => setWhatsappClient(client)} className="flex items-center gap-1 text-[10px] font-extrabold text-cyan-600 hover:text-cyan-700">
                  <Send className="h-3 w-3" /> Mensaje Rápido
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Message Drawer (#1, #8, #10) */}
      {whatsappClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setWhatsappClient(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-1.5">
              <MessageSquare className="h-5 w-5 text-cyan-500" /> Envío Campaña WhatsApp
            </h3>
            <p className="text-xs text-slate-500 mb-4">Enviar mensaje personalizado a {whatsappClient.firstName} {whatsappClient.lastName}</p>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold text-slate-500 mb-1 block">Plantilla de Campaña</label>
                <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              {selectedTemplateId && (
                <div className="border border-slate-100 rounded-xl p-3 bg-slate-50 space-y-2">
                  <img src={templates.find(t=>t.id===selectedTemplateId)?.imageUrl} className="h-24 w-full object-cover rounded-lg border" />
                  <p className="text-xs text-slate-600 leading-relaxed italic">
                    "{templates.find(t=>t.id===selectedTemplateId)?.content.replace('{{name}}', whatsappClient.firstName)}"
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setWhatsappClient(null)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={simulateSendMessage} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition">Enviar por Meta API</button>
            </div>
          </div>
        </div>
      )}

      {/* Creation Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4">Registrar Nuevo Cliente</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre *</label>
                  <input type="text" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Apellido *</label>
                  <input type="text" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Teléfono / WhatsApp</label>
                <input type="text" placeholder="+569..." className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Email</label>
                <input type="email" placeholder="cliente@correo.com" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Etiquetas (separadas por coma)</label>
                <input type="text" placeholder="VIP, Facial, Depilación" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={tagInput} onChange={e => setTagInput(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas médicas o preferencias</label>
                <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreate} disabled={!firstName || !lastName} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Cliente</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
